import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { changePassword } from "../api";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import theme from "../styles/theme";

export default function ChangePasswordPage() {
  const [username, setUsername] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validation = useMemo(() => {
    const trimmedUsername = username.trim();
    if (!trimmedUsername) return { ok: false, message: "Le pseudo est requis." };
    if (!oldPassword) return { ok: false, message: "L'ancien mot de passe est requis." };
    if (!newPassword) return { ok: false, message: "Le nouveau mot de passe est requis." };
    if (newPassword.length < 8) return { ok: false, message: "Le mot de passe doit contenir au moins 8 caractères." };
    if (confirmPassword !== newPassword) return { ok: false, message: "La confirmation ne correspond pas." };
    return { ok: true, message: "" };
  }, [username, oldPassword, newPassword, confirmPassword]);

  const handleChangePassword = async () => {
    setError("");
    setSuccess("");
    try {
      if (!validation.ok) {
        setError(validation.message);
        return;
      }

      setLoading(true);
      const res: any = await changePassword(username.trim(), oldPassword, newPassword);
      if (res.success) {
        setSuccess("Mot de passe modifié avec succès !");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        setError(res.error || "Erreur lors de la modification du mot de passe");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 140px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        pb: 10,
      }}
    >
      <Paper elevation={6} sx={{ width: "100%", maxWidth: 520, p: { xs: 3, sm: 4 } }}>
        <Stack spacing={2.5}>
          <Stack spacing={0.5}>
            <Typography variant="h2" sx={{ fontSize: { xs: "1.6rem", sm: "2rem" } }}>
              Changer mon mot de passe
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Saisis ton pseudo, ton ancien mot de passe, puis choisis un nouveau mot de passe.
            </Typography>
          </Stack>

          <Box
            sx={{
              borderRadius: 2,
              p: 2,
              bgcolor: theme.palette.background.paperColored,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Première connexion ? Utilise le lien d’activation fourni par le coach.
            </Typography>
          </Box>

          <TextField
            label="Pseudo"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            fullWidth
          />

          <TextField
            label="Ancien mot de passe"
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            autoComplete="current-password"
            fullWidth
            required
          />

          <TextField
            label="Nouveau mot de passe"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            fullWidth
            helperText="Au moins 8 caractères."
          />

          <TextField
            label="Confirmer le nouveau mot de passe"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            fullWidth
          />

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleChangePassword}
            disabled={loading}
            sx={{ py: 1.25, fontWeight: 700 }}
          >
            {loading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} color="inherit" />
                <span>Enregistrement…</span>
              </Stack>
            ) : (
              "Mettre à jour le mot de passe"
            )}
          </Button>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
        </Stack>
      </Paper>
    </Box>
  );
}
